import React, {
  useState,
  useEffect,
  Suspense,
  lazy,
  startTransition,
} from 'react';
// import { StatusBar } from 'expo-status-bar';
import { AppRegistry, Platform } from 'react-native';
import * as Font from 'expo-font';
import { PaperProvider } from 'react-native-paper';
import {
  SafeAreaProvider,
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import appInfo from './app.json';
import { lightTheme, darkTheme } from './src/utils/theme';
import { store } from './src/store/store';
import { Provider, useSelector, useDispatch } from 'react-redux';
import Toast from 'react-native-toast-message';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { setLightTheme } from './src/store/slice/themeSlice';
import * as SplashScreen from 'expo-splash-screen';
import './i18nextInit';
import {
  getRoleLocalStorage,
  getToken,
  setRoleLocalStorage,
  setToken,
} from './src/utils';
import { UserApi } from './src/store/api/UserApi';
import { setUser } from './src/store/slice/userSlice';
import { registerServiceWorker } from './serviceWorker';
import { setRole } from './src/store/slice/roleSlice';
import * as Sentry from 'sentry-expo';
import { SENTRY_DSN_URL, SENTRY_EXPO_DEVELOPMENT, SENTRY_DEBUG } from '@env';
import { messaging } from './firebase';
import { onMessage } from 'firebase/messaging';
import { toastConfig } from './src/config/ToastConfig';
import Constants from 'expo-constants';
import ErrorBoundary from './src/components/ErrorBoundary';
import { QRScannerProvider } from './src/contexts/ScannerContext';

Sentry.init({
  dsn: SENTRY_DSN_URL,
  enableInExpoDevelopment: SENTRY_EXPO_DEVELOPMENT,
  debug: SENTRY_DEBUG,
  tracesSampleRate: 1.0,
});

SplashScreen.preventAutoHideAsync();

// Global error handler for Suspense errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (
      event.error &&
      event.error.message &&
      event.error.message.includes(
        'suspended while responding to synchronous input'
      )
    ) {
      console.warn('Suspense error detected, attempting to recover...');
      // Prevent the error from being thrown
      event.preventDefault();
    }
  });

  // Set dark background to prevent white flashes
  document.body.style.backgroundColor = '#121212';
  document.body.style.margin = '0';
  document.body.style.padding = '0';
}

const Navigations = lazy(() => import('./src/navigations'));
const Loader = lazy(() => import('./src/components/Loader'));
const PNotification = lazy(() => import('./src/components/PNotification'));

// Dark loading component to prevent white flashes
const DarkLoader = () => (
  <div
    style={{
      backgroundColor: '#121212',
      color: '#ffffff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      margin: 0,
      padding: 0,
      fontFamily: 'Poppins, sans-serif',
    }}
  >
    <div
      style={{
        textAlign: 'center',
        fontSize: '16px',
        color: '#ffffff',
      }}
    >
      Loading...
    </div>
  </div>
);

function App() {
  const [isShowNotification, setIsShowNotification] = useState(false);
  const [notification, setNotification] = useState({ title: '', body: '' });
  const isLoading = useSelector((state) => state.isLoading);
  const isLightTheme = useSelector((state) => state.isLightTheme);
  const [isLoadAssetComplete, setLoadAssetComplete] = useState(false);
  const currentRole = useSelector((state) => state.role);
  const currentUserDetail = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();

  // TODO: Re-enable foreground notification popup if needed in future
  // Disabled to reduce UI interruptions - notifications still work in background
  /* 
  const onMessageListener = (async () => {
    const messagingResolve = await messaging;
    if (messagingResolve) {
      onMessage(messagingResolve, (payload) => {
        setIsShowNotification(true);
        setNotification({
          title: payload.notification.title,
          body: payload.notification.body,
        });
      });
    }
  })();
  */

  useEffect(() => {
    const localVersion = localStorage.getItem('app-version');
    const currentVersion = document
      .querySelector('meta[name="app-version"]')
      ?.getAttribute('content');

    if (localVersion && localVersion !== currentVersion) {
      localStorage.setItem('app-version', currentVersion);
      window.location.reload();
    } else {
      localStorage.setItem('app-version', currentVersion);
    }
  }, []);

  useEffect(() => {
    async function prepare() {
      try {
        const token = await getToken();
        if (token) {
          const userDetailResponse = await dispatch(
            UserApi.endpoints.getUserDetail.initiate()
          );
          if (userDetailResponse && userDetailResponse?.data) {
            dispatch(setUser(userDetailResponse?.data));
          }
        }
        let rLightTheme = await AsyncStorage.getItem('isLightTheme');
        if (token && rLightTheme == 'true') {
          store.dispatch(setLightTheme(true));
        } else {
          store.dispatch(setLightTheme(false));
        }
        let role = await getRoleLocalStorage();
        if (role) {
          if (role === 'Owner') {
            role = 'business';
            await setRoleLocalStorage('business');
          }
          if (role === 'User') {
            role = 'user';
            await setRoleLocalStorage('user');
          }
          store.dispatch(setRole(role));
        } else {
          store.dispatch(setRole('user'));
        }

        await Promise.all([
          Font.loadAsync({
            Poppins: require('./src/assets/fonts/Poppins-Regular.ttf'),
          }),
        ]);

        await SplashScreen.hideAsync();
      } catch (e) {
        console.warn(e);
      } finally {
        setLoadAssetComplete(true);
      }
    }

    prepare();
  }, []);

  if (!isLoadAssetComplete) {
    if (Platform.OS === 'web') {
      return (
        <SafeAreaView
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#121212',
          }}
        >
          <Suspense fallback={<DarkLoader />}>
            <Loader />
          </Suspense>
        </SafeAreaView>
      );
    } else {
      return null;
    }
  }

  return (
    <>
      <PaperProvider theme={isLightTheme ? lightTheme : darkTheme}>
        <SafeAreaProvider>
          <QRScannerProvider>
            <SafeAreaView
              style={{
                flex: 1,
                paddingTop: Constants.statusBarHeight,
                backgroundColor: '#121212',
              }}
            >
              {/* <StatusBar style="light" /> */}
              <ErrorBoundary
                onRefresh={() => window.location.reload()}
                onGoBack={() => window.history.back()}
              >
                <Suspense fallback={<DarkLoader />}>
                  <Navigations />
                </Suspense>
              </ErrorBoundary>
              {/* TODO: Re-enable if foreground notification popup is needed
              <PNotification
                notification={notification}
                isShowNotification={isShowNotification}
                hideNotification={() => setIsShowNotification(false)}
              />
              */}
            </SafeAreaView>
          </QRScannerProvider>
        </SafeAreaProvider>
      </PaperProvider>
      <Toast config={toastConfig} />
      {isLoading && (
        <Suspense fallback={<DarkLoader />}>
          <Loader />
        </Suspense>
      )}
    </>
  );
}

export default function AppWrapper() {
  const handleAppRefresh = () => {
    // Refresh the entire app
    if (Platform.OS === 'web') {
      window.location.reload();
    }
  };

  return (
    <SafeAreaProvider>
      <Provider store={store}>
        <ErrorBoundary
          onRefresh={handleAppRefresh}
          onGoBack={() => {
            // For app-level errors, we can't go back, so just refresh
            handleAppRefresh();
          }}
        >
          <App />
        </ErrorBoundary>
      </Provider>
    </SafeAreaProvider>
  );
}

AppRegistry.registerComponent(appInfo.expo.name, () => AppWrapper);
registerServiceWorker();
